import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Description as FileIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_DOCUMENTS_BY_COMPANY_PAGED } from '../graphql/queries';
import { useCompany } from '../context/CompanyContext';

const Documents = () => {
  const navigate = useNavigate();
  const { activeCompanyId } = useCompany();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchTerm.trim();
      setDebouncedSearch((prev) => {
        if (prev !== next) {
          setPage(0);
        }
        return next;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [activeCompanyId, dateFrom, dateTo]);

  const { data, loading } = useQuery(GET_DOCUMENTS_BY_COMPANY_PAGED, {
    variables: {
      companyId: activeCompanyId,
      page,
      size: rowsPerPage,
      search: debouncedSearch || null,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    },
    skip: !activeCompanyId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const pageData = data?.documentsByCompanyPaged;
  const documents = pageData?.content || [];
  const totalElements = pageData?.totalElements || 0;

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getStatusChip = (status, isPaid) => {
    if (status === 'CANCELLED') {
      return <Chip label="Анулирана" color="error" size="small" variant="outlined" />;
    }
    if (status === 'DRAFT') {
      return <Chip label="Чернова" color="default" size="small" variant="outlined" />;
    }
    if (isPaid) {
      return <Chip label="Платена" color="success" size="small" />;
    }
    return <Chip label="Издадена" color="primary" size="small" />;
  };

  const getDocumentTypeLabel = (type) => {
    switch (type) {
      case 'INVOICE': return 'Фактура';
      case 'PROFORMA': return 'Проформа';
      case 'CREDIT_NOTE': return 'Кредитно';
      case 'DEBIT_NOTE': return 'Дебитно';
      default: return type;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Документи
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/documents/create')}
          size="large"
        >
          Нов документ
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 2, p: 2 }}>
        {/* Toolbar */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Търсене по номер или клиент..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="От дата"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: dateTo || undefined }}
            sx={{ width: 170 }}
          />
          <TextField
            label="До дата"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: dateFrom || undefined }}
            sx={{ width: 170 }}
          />
          {(searchTerm || dateFrom || dateTo) && (
            <Button
              variant="text"
              size="small"
              startIcon={<ClearIcon />}
              onClick={() => {
                setSearchTerm('');
                setDebouncedSearch('');
                setDateFrom('');
                setDateTo('');
                setPage(0);
              }}
            >
              Изчисти
            </Button>
          )}
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <TableHead>
              <TableRow>
                <TableCell>Тип</TableCell>
                <TableCell>Номер</TableCell>
                <TableCell>Дата</TableCell>
                <TableCell>Клиент</TableCell>
                <TableCell align="right">Сума</TableCell>
                <TableCell align="center">Статус</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography color="textSecondary">Няма намерени документи</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((row) => (
                  <TableRow
                    hover
                    key={row.id}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/documents/${row.id}`)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileIcon color="action" fontSize="small" />
                        {getDocumentTypeLabel(row.documentType)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{row.documentNumber}</TableCell>
                    <TableCell>{new Date(row.issueDate).toLocaleDateString('bg-BG')}</TableCell>
                    <TableCell>{row.clientName}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {Number(row.totalAmountWithVat || 0).toFixed(2)} {row.currencyCode}
                    </TableCell>
                    <TableCell align="center">
                      {getStatusChip(row.status, row.isPaid)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Преглед">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/documents/${row.id}`); }}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Редакция">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/documents/${row.id}/edit`); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Редове на страница:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} от ${count !== -1 ? count : `повече от ${to}`}`
          }
        />
      </Paper>
    </Box>
  );
};

export default Documents;